'use client'

import { MouseEvent } from 'react'
import { Cell, Icon, Text } from '@vinted/web-ui'
import { DonateFilled16 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { donationsClickEvent } from '@marketplace-web/donations/fundraisers-data'

import FundraiserInfoBlock from './FundraiserInfoBlock'

const ScreenToTarget = {
  user_profile: 'learn_about_donations_from_wardrobe',
  user_info: 'learn_about_donations_from_user_about_section',
}

type UserFundraiserDto = {
  login: string
  fundraiser: {
    active: boolean
    percentage: number | null
    feature_disabled?: boolean | null
  } | null
}

type Props = {
  user: UserFundraiserDto
  screen: string
  styling?: React.ComponentProps<typeof Cell>['styling']
}

const FundraiserInfo = ({ user, screen, styling }: Props) => {
  const translate = useTranslate('user')
  const { track } = useTracking()

  const handleClick = (event: MouseEvent<Element>) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    const trackingEvent = donationsClickEvent({
      screen,
      target: ScreenToTarget[screen],
    })

    track(trackingEvent)
  }

  if (!user.fundraiser?.active) return null
  if (user.fundraiser.feature_disabled) return null

  return (
    <HelpCenterFaqEntryUrl
      type={FaqEntryType.Donations}
      render={url => (
        <Cell onClick={handleClick} styling={styling} testId="fundraiser-info">
          <FundraiserInfoBlock iconName={DonateFilled16} iconColor={Icon.Color.Success}>
            <Text
              as="span"
              text={translate('donating', {
                username: user.login,
                'faq-url': chunk => (
                  <a key="faq-url" href={url}>
                    {chunk}
                  </a>
                ),
              })}
            />
          </FundraiserInfoBlock>
        </Cell>
      )}
    />
  )
}

export default FundraiserInfo
